import { Link } from "@tanstack/react-router";
import { SafeExternalLink } from "@/components/SafeExternalLink";
import { PaywallBadge } from "@/components/PaywallBadge";
import { SaveButton } from "@/components/SaveButton";
import { EditorialImage } from "@/components/EditorialImage";
import { OutboundArrow } from "@/components/OutboundArrow";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";
import { detectFormat, formatLabel } from "@/lib/format-detector";
import { renderWithWikiLinks } from "@/lib/wiki-link";
import { getMotifFallback } from "@/lib/motif-fallback";
import { normalizeTitle } from "@/lib/normalize-title";


type Article = {
  id: string;
  original_url: string;
  title: string;
  summary?: string | null;
  selection_reason?: string | null;
  source: string;
  topic: string;
  paywall: boolean;
  image_url?: string | null;
};

function prettySourceName(source: string, url: string): string {
  if (source && !/^https?:\/\//i.test(source)) return source;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return source || "kilden";
  }
}

/**
 * Ugens strejf – kompakt liste: 1:1-thumb, kicker, titel, kort
 * "Derfor:"-linje, kilde+format. "+ Gem" og outbound ↗ er to
 * adskilte tap-mål, aldrig overlappende.
 */
export function StrejfList({
  items,
  onOpen,
}: {
  items: Article[];
  onOpen?: (id: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className="rule-double py-12 md:py-[52px]" aria-label="Ugens strejf">
      <div className="flex flex-wrap items-baseline justify-between gap-y-1 gap-x-4 border-b-2 border-ink pb-3.5 mb-1">
        <h2 className="font-serif font-normal text-[26px] md:text-[32px] m-0 text-ink">
          Ugens strejf
        </h2>
        <span className="font-serif italic text-[13px] md:text-[15px] text-muted">
          udvalgt – ikke alt, kun det gode
        </span>
      </div>

      <ul className="m-0 p-0 list-none">
        {items.map((a) => {
          const derfor = a.selection_reason?.trim();
          const fmt = detectFormat(a.original_url, a.title);
          return (
            <li
              key={a.id}
              className="border-b border-rule"
            >
              <div className="grid grid-cols-[64px_1fr] md:grid-cols-[88px_1fr_auto] items-start gap-x-4 md:gap-x-[26px] py-4 md:py-5">
                <SafeExternalLink
                  href={a.original_url}
                  onOpen={() => onOpen?.(a.id)}
                  className="block group"
                  aria-label={`Læs "${a.title}" hos ${prettySourceName(a.source, a.original_url)}`}
                >
                  <EditorialImage
                    src={a.image_url ?? null}
                    alt=""
                    ratio="1/1"
                    width={88}
                    height={88}
                    fallback={getMotifFallback("article", a.topic)}
                  />

                </SafeExternalLink>

                <div className="min-w-0">
                  <SafeExternalLink
                    href={a.original_url}
                    onOpen={() => onOpen?.(a.id)}
                    className="group block min-w-0"
                  >
                    <span className="inline-flex items-center gap-2 mb-1.5">
                      <span className="eyebrow">
                        {TOPIC_LABEL[a.topic as Topic] ?? "Fund"}
                      </span>
                      <PaywallBadge paywall={a.paywall} />
                    </span>
                    <span className="block font-serif text-[19px] md:text-[22px] leading-[1.22] text-ink group-hover:text-accent transition-colors mb-1.5">
                      {normalizeTitle(a.title)}
                    </span>
                    {derfor && (
                      <span className="block font-serif italic font-light text-[14px] md:text-[15px] leading-[1.5] text-ink-soft">
                        <span className="not-italic font-medium text-accent">
                          Derfor:{" "}
                        </span>
                        {renderWithWikiLinks(derfor)}
                      </span>
                    )}
                  </SafeExternalLink>

                  {/* Mobil: bundlinje med kilde + Gem, matcher referencen */}
                  <div className="mt-2.5 flex items-center justify-between gap-3 md:hidden">
                    <SafeExternalLink
                      href={a.original_url}
                      onOpen={() => onOpen?.(a.id)}
                      className="font-serif text-[12px] italic text-muted hover:text-accent transition-colors min-w-0"
                    >
                      <span className="whitespace-nowrap">
                        {prettySourceName(a.source, a.original_url)}
                        {fmt !== "article" ? ` · ${formatLabel(fmt)}` : ""}
                        <OutboundArrow />
                      </span>
                    </SafeExternalLink>
                    <SaveButton id={a.id} compact />
                  </div>
                </div>

                {/* Desktop: højre rail */}
                <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0">
                  <SaveButton id={a.id} compact />
                  <SafeExternalLink
                    href={a.original_url}
                    onOpen={() => onOpen?.(a.id)}
                    className="font-serif text-[13px] italic text-muted hover:text-accent transition-colors"
                  >
                    <span className="whitespace-nowrap">
                      {prettySourceName(a.source, a.original_url)}
                      {fmt !== "article" ? ` · ${formatLabel(fmt)}` : ""}
                      <OutboundArrow />
                    </span>
                  </SafeExternalLink>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-5">
        <span className="font-serif italic text-[13px] md:text-[14px] text-muted">
          Alle henvisninger fører til den oprindelige kilde
        </span>
        <Link
          to="/artikler"
          className="font-serif italic text-[15px] md:text-[16px] text-accent hover:text-accent-dark hover:underline underline-offset-4 transition-colors"
        >
          Se ugens strejf →
        </Link>
      </div>
    </section>
  );
}
